import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckboxGroup, Loader } from "@threadws/jute";
import type { UserWithPersonas } from "../../api/generated";
import {
  meMeOptions,
  usersListOptions,
  usersListQueryKey,
  usersSetPersonasMutation,
} from "../../api/generated/@tanstack/react-query.gen";
import { userName } from "../../utils/userName";

/** Who this persona belongs to, as a checkbox per person. Anyone can add or remove anyone. */
export function PersonaPeople({ personaId }: { personaId: string }) {
  const queryClient = useQueryClient();
  const { data: me } = useQuery(meMeOptions());
  const { data: users, isPending, error } = useQuery(usersListOptions());
  const save = useMutation({
    ...usersSetPersonasMutation(),
    onSuccess: (saved) =>
      queryClient.setQueryData<UserWithPersonas[]>(usersListQueryKey(), (list) =>
        list?.map((user) => (user.id === saved.id ? saved : user)),
      ),
  });

  // The group reports the whole new selection; find the one person whose box changed.
  const change = (selected: string[]) => {
    if (save.isPending || !users) return;
    const user = users.find((u) => u.personaIds.includes(personaId) !== selected.includes(u.id));
    if (!user) return;
    const personaIds = selected.includes(user.id)
      ? [...user.personaIds, personaId]
      : user.personaIds.filter((id) => id !== personaId);
    save.mutate({ path: { id: user.id }, body: { personaIds } });
  };

  return (
    <section className="flex flex-col gap-4">
      <div>
        <h2 className="text-xl font-medium">People</h2>
        <p className="text-sm text-text-secondary">
          Whose persona this is. Everyone can see every persona; features like calendar clashes only look at your own.
        </p>
      </div>
      {isPending ? (
        <Loader label="Loading people" />
      ) : error ? (
        <p className="text-danger">Couldn't load people: {error.message}</p>
      ) : (
        <CheckboxGroup
          options={users.map((user) => ({
            value: user.id,
            label: user.id === me?.id ? `${userName(user)} (you)` : userName(user),
          }))}
          value={users.filter((user) => user.personaIds.includes(personaId)).map((user) => user.id)}
          onChange={change}
        />
      )}
      {save.error && <p className="text-danger">Couldn't save: {save.error.message}</p>}
    </section>
  );
}
